import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

const MENU_WIDTH = 200;
const MENU_ITEM_HEIGHT = 37;
const MENU_PADDING = 8;

const ContextMenu = ({
  position,
  foldered,
  selectedFolderedItems,
  team,
  currentUser,
  onClose,
  onDownload,
  onCopy,
  onMove,
  onRename,
  onSetPermissions,
  onShare,
  onDelete
}) => {
  const menuRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleScroll = () => onClose();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    if (!menuRef.current) return;

    const rect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let x = position.x;
    let y = position.y;

    if (x + rect.width > viewportWidth - 8) {
      x = viewportWidth - rect.width - 8;
    }

    if (y + rect.height > viewportHeight - 8) {
      y = viewportHeight - rect.height - 8;
    }

    if (x !== position.x || y !== position.y) {
      setAdjustedPosition({ x, y });
    }
  }, [position]);

  const isMultiSelect = selectedFolderedItems.some((fi) => fi.id === foldered.id)
    && selectedFolderedItems.length > 1;
  const itemCount = isMultiSelect ? selectedFolderedItems.length : 1;
  const isFolder = foldered.item.is_folder;
  const canManage = currentUser.can_manage_calendar_event_attachments && foldered.item.user_can_change;

  const menuStyle = {
    top: adjustedPosition.y,
    left: adjustedPosition.x
  };

  return (
    <div ref={menuRef} className="asset-center-context-menu" style={menuStyle}>
      {!isFolder && !isMultiSelect ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onDownload(foldered); }}>
          <span className="context-menu-icon"><i className="far fa-arrow-to-bottom fa-fw"></i></span>
          Download
        </div>
      ) : null}
      {isFolder && foldered.item.can_download && !isMultiSelect ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onDownload(foldered); }}>
          <span className="context-menu-icon"><i className="far fa-arrow-to-bottom fa-fw"></i></span>
          Download Folder
        </div>
      ) : null}
      {!isFolder && !isMultiSelect ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onCopy(foldered); }}>
          <span className="context-menu-icon"><i className="far fa-copy fa-fw"></i></span>
          Copy
        </div>
      ) : null}
      {canManage ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onMove(foldered, isMultiSelect); }}>
          <span className="context-menu-icon"><i className="fas fa-arrows-alt fa-fw"></i></span>
          Move{isMultiSelect ? " " + itemCount + " items" : ""}
        </div>
      ) : null}
      {canManage && !isMultiSelect ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onRename(foldered); }}>
          <span className="context-menu-icon" style={{ color: "#1982C4" }}><i className="fas fa-pencil fa-fw"></i></span>
          Rename
        </div>
      ) : null}
      {canManage && !isMultiSelect ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onShare(foldered); }}>
          <span className="context-menu-icon"><i className="far fa-link fa-fw"></i></span>
          Share
        </div>
      ) : null}
      {currentUser.can_manage_permission_sets && !isMultiSelect ? (
        <div className="context-menu-item"
             onClick={() => { onClose(); onSetPermissions(foldered); }}>
          <span className="context-menu-icon" style={{ color: "#6c757d" }}><i className="far fa-user-lock fa-fw"></i></span>
          Set Permissions
        </div>
      ) : null}
      {canManage ? (
        <React.Fragment>
          <div className="context-menu-divider"></div>
          <div className="context-menu-item context-menu-item-danger"
               onClick={() => { onClose(); onDelete(foldered, isMultiSelect); }}>
            <span className="context-menu-icon"><i className="fas fa-trash fa-fw"></i></span>
            Delete{isMultiSelect ? " " + itemCount + " items" : ""}
          </div>
        </React.Fragment>
      ) : null}
    </div>
  );
};

ContextMenu.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }).isRequired,
  foldered: PropTypes.object.isRequired,
  selectedFolderedItems: PropTypes.array.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onSetPermissions: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired
};

export default ContextMenu;
